import { z } from 'zod';
import { SuiteKindSchema } from './suite.js';
// ─── Enums ──────────────────────────────────────────────────────────────────
export const FeedbackSentimentSchema = z.enum(['POSITIVE', 'NEGATIVE']);
export const FeedbackStatusSchema = z.enum(['PENDING', 'PROMOTED', 'DISMISSED']);
// ─── Feedback ───────────────────────────────────────────────────────────────
export const FeedbackSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    runId: z.string(),
    sentiment: FeedbackSentimentSchema,
    comment: z.string().nullable(),
    correctedOutput: z.record(z.string(), z.unknown()).nullable(),
    status: FeedbackStatusSchema,
    submittedBy: z.string(),
    promotedAt: z.string().datetime().nullable(),
    promotedBy: z.string().nullable(),
    groundTruthId: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    deletedAt: z.string().datetime().nullable(),
});
// ─── Create ─────────────────────────────────────────────────────────────────
export const CreateFeedbackInputSchema = z.object({
    sentiment: FeedbackSentimentSchema,
    comment: z.string().max(5000).optional(),
    /**
     * The corrected output for NEGATIVE feedback. Required for the feedback to
     * mint an eval task (DEV-16834/16837): NEGATIVE without a correction is
     * accepted but parked as a PENDING flag ("wrong, but I don't know the
     * answer") — it never mints ground truth.
     */
    correctedOutput: z.record(z.string(), z.unknown()).optional(),
});
/**
 * Eval lineage minted by submit-and-mint (DEV-16834). `null` on the response
 * when the feedback was parked instead of minted (eval run, negative without
 * correction, or run without output).
 */
export const MintedEvalLineageSchema = z.object({
    groundTruthId: z.string(),
    /** Null when an equivalent task already covered this GT in the routed suite. */
    taskId: z.string().nullable(),
    suiteId: z.string(),
    suiteKind: SuiteKindSchema,
    /** True when an existing GT for the same input was reused (positive dedup). */
    reusedGroundTruth: z.boolean(),
    /** Set when a negative correction superseded a prior GT (tasks re-linked). */
    supersededGroundTruthId: z.string().nullable(),
});
export const CreateFeedbackResponseSchema = FeedbackSchema.extend({
    minted: MintedEvalLineageSchema.nullable(),
});
/**
 * Mint an eval task directly from a run (DEV-16839, no feedback row).
 * Omitting `correctedOutput` protects the run's own output (Regression
 * suite); providing it captures a corrected expectation (Capability suite).
 */
export const MintEvalTaskInputSchema = z.object({
    correctedOutput: z.record(z.string(), z.unknown()).optional(),
});
// ─── Update (PENDING only) ──────────────────────────────────────────────────
export const UpdateFeedbackInputSchema = z.object({
    sentiment: FeedbackSentimentSchema.optional(),
    comment: z.string().max(5000).nullable().optional(),
    correctedOutput: z.record(z.string(), z.unknown()).nullable().optional(),
});
// ─── Promote ────────────────────────────────────────────────────────────────
export const PromoteFeedbackInputSchema = z.object({
    correctedOutput: z.record(z.string(), z.unknown()).optional(),
    supersede: z.boolean().optional(),
});
// ─── Dismiss ────────────────────────────────────────────────────────────────
export const DismissFeedbackInputSchema = z.object({
    reason: z.string().max(5000).optional(),
});
// ─── Bulk operations ────────────────────────────────────────────────────────
export const BulkFeedbackActionInputSchema = z.object({
    ids: z.array(z.string()).min(1).max(100),
});
//# sourceMappingURL=feedback.js.map